import { useEffect } from 'react'
import { doc } from 'firebase/firestore'
import type { DocumentReference } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { getDoc, setDoc } from '../lib/firestore'
import { log } from '../lib/logger'
import type { UserActivity } from '../types'
import { useAuth } from './useAuth'

const STAMPED_DAY_KEY = 'beacon_last_active_day'

function readStampedDay(uid: string): string | null {
  try {
    return localStorage.getItem(`${STAMPED_DAY_KEY}:${uid}`)
  } catch {
    return null
  }
}

function writeStampedDay(uid: string, day: string) {
  try {
    localStorage.setItem(`${STAMPED_DAY_KEY}:${uid}`, day)
  } catch {
    // Without the flag we just re-read the doc on the next open.
  }
}

/**
 * Stamps `users/{uid}/private/activity` once per local day for a signed-in
 * user — on app open and whenever it comes back to the foreground, since the
 * native app can sit in the background for days. The localStorage flag skips
 * the read entirely after today's stamp.
 */
export function useLastActiveStamp() {
  const { signedInUid } = useAuth()

  useEffect(() => {
    if (!signedInUid) return
    const uid = signedInUid
    const ref = doc(db, 'users', uid, 'private', 'activity') as DocumentReference<UserActivity>
    let inFlight = false

    const stamp = () => {
      if (inFlight || document.visibilityState !== 'visible') return
      const today = new Date().toDateString()
      if (readStampedDay(uid) === today) return
      inFlight = true
      getDoc(ref)
        .then(snap => {
          const last = snap.data()?.lastActiveAt
          if (last !== undefined && new Date(last).toDateString() === today) return
          return setDoc(
            ref,
            { lastActiveAt: Date.now(), ...(last !== undefined && { previousActiveAt: last }) },
            { merge: true },
          )
        })
        .then(() => writeStampedDay(uid, today))
        .catch(() => log('activity', 'failed to stamp last active day'))
        .finally(() => {
          inFlight = false
        })
    }

    stamp()
    document.addEventListener('visibilitychange', stamp)
    return () => document.removeEventListener('visibilitychange', stamp)
  }, [signedInUid])
}
